import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Loader2, Ruler } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { cancelLocalAlert, localAlerts } from "@/lib/priceAlerts";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const { country, currency, setCountry, setCurrency, countries, currencies, formatMoney } = useLocale();
  const p = user?.profile || {};
  const [form, setForm] = useState({
    height_cm: p.height_cm || "", weight_kg: p.weight_kg || "",
    chest_cm: p.chest_cm || "", waist_cm: p.waist_cm || "", hips_cm: p.hips_cm || "",
    size: p.size || "M", style_preferences: p.style_preferences || "",
    country: p.country || country, currency: p.currency || currency,
    address_name: p.address_name || "", address_line1: p.address_line1 || "",
    address_line2: p.address_line2 || "", address_city: p.address_city || "",
    address_region: p.address_region || "", address_postal: p.address_postal || "",
    address_country: p.address_country || p.country || country,
    address_phone: p.address_phone || "",
  });
  const [saving, setSaving] = useState(false);
  const [alerts, setAlerts] = useState(localAlerts().filter((a) => a.status !== "cancelled"));
  const [removingId, setRemovingId] = useState("");

  useEffect(() => {
    api.get("/price-alerts")
      .then(({ data }) => {
        if (data?.alerts) setAlerts(data.alerts.filter((a) => a.status !== "cancelled"));
      })
      .catch(() => setAlerts(localAlerts().filter((a) => a.status !== "cancelled")));
  }, []);

  const removeAlert = async (alert) => {
    const id = alert?.id;
    if (!id || removingId) return;
    setRemovingId(id);
    cancelLocalAlert(alert.listing_id);
    setAlerts((rows) => rows.filter((a) => a.id !== id));
    try {
      if (!String(id).startsWith("local:")) {
        await api.delete(`/price-alerts/${id}`);
      }
      toast.success("Price alert removed");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRemovingId("");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        chest_cm: form.chest_cm ? Number(form.chest_cm) : null,
        waist_cm: form.waist_cm ? Number(form.waist_cm) : null,
        hips_cm: form.hips_cm ? Number(form.hips_cm) : null,
      };
      await updateProfile(payload);
      if (form.country) setCountry(form.country, { syncCurrency: false });
      if (form.currency) setCurrency(form.currency);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    ["Height (cm)", "height_cm"], ["Weight (kg)", "weight_kg"],
    ["Chest (cm)", "chest_cm"], ["Waist (cm)", "waist_cm"], ["Hips (cm)", "hips_cm"],
  ];

  const isSeller = user?.role === "seller";

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="flex items-center gap-2 text-[11px] tracking-luxe uppercase text-aura-accent mb-3">
        <Ruler className="w-3.5 h-3.5" /> {isSeller ? "Shop profile" : "Fit & taste"}
      </div>
      <h1 className="font-serif text-5xl tracking-tight mb-3">{isSeller ? "Profile & shipping" : "Your fit profile"}</h1>
      <p className="text-sm text-aura-muted mb-4">
        {isSeller
          ? "Save your ship-from address (especially postal code) so marketplace listings like Etsy can create a shipping profile."
          : "Add your usual size and taste so boutique listings can show a simple fit check."}
      </p>
      <PasswordForm />

      {isSeller && user?.subscription ? (
        <p className="text-sm text-aura-muted mb-12">
          Plan: {user.subscription.plan_id || "none"} · {user.subscription.status}
          {user.needs_billing ? " — payment due." : ""}
        </p>
      ) : (
        <div className="mb-8" />
      )}

      <div className="bg-aura-surface border border-aura-border p-8 space-y-6">
        {isSeller && (
          <>
            <div>
              <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-3">Shipping address</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={form.address_name} onChange={set("address_name")} placeholder="Full name" data-testid="profile-addr-name" className="sm:col-span-2 px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_line1} onChange={set("address_line1")} placeholder="Address line 1" data-testid="profile-addr-line1" className="sm:col-span-2 px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_line2} onChange={set("address_line2")} placeholder="Address line 2" className="sm:col-span-2 px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_city} onChange={set("address_city")} placeholder="City" data-testid="profile-addr-city" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_region} onChange={set("address_region")} placeholder="State / region" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_postal} onChange={set("address_postal")} placeholder="Postal code" data-testid="profile-addr-postal" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_phone} onChange={set("address_phone")} placeholder="Mobile" data-testid="profile-addr-phone" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <select value={form.address_country} onChange={set("address_country")} data-testid="profile-addr-country" className="px-3 py-2.5 border border-aura-border outline-none text-sm bg-aura-surface">
                  {(countries || []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Country</label>
                <select value={form.country} onChange={set("country")} data-testid="profile-country" className="mt-2 w-full px-3 py-2.5 border border-aura-border outline-none text-sm bg-aura-surface">
                  {(countries || []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Currency</label>
                <select value={form.currency} onChange={set("currency")} data-testid="profile-currency" className="mt-2 w-full px-3 py-2.5 border border-aura-border outline-none text-sm bg-aura-surface">
                  {(currencies || []).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
                </select>
              </div>
            </div>
          </>
        )}

        <div>
          <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Usual size</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {SIZES.map((s) => (
              <button
                key={s}
                onClick={() => setForm((f) => ({ ...f, size: s }))}
                data-testid={`size-${s}`}
                className={`w-12 py-2 text-sm border transition-colors ${
                  form.size === s ? "border-aura-accent bg-aura-accent text-white" : "border-aura-border hover:border-aura-accent"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {fields.map(([label, key]) => (
            <div key={key}>
              <label className="text-[10px] tracking-luxe uppercase text-aura-muted">{label}</label>
              <input
                type="number" value={form[key]} onChange={set(key)}
                data-testid={`profile-${key}`}
                className="mt-2 w-full px-3 py-2.5 border border-aura-border focus:border-aura-accent outline-none text-sm font-mono"
              />
            </div>
          ))}
        </div>

        {!isSeller && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Country</label>
                <select value={form.country} onChange={set("country")} data-testid="profile-country" className="mt-2 w-full px-3 py-2.5 border border-aura-border outline-none text-sm bg-aura-surface">
                  {(countries || []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Currency</label>
                <select value={form.currency} onChange={set("currency")} data-testid="profile-currency" className="mt-2 w-full px-3 py-2.5 border border-aura-border outline-none text-sm bg-aura-surface">
                  {(currencies || []).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
                </select>
              </div>
            </div>

            <div>
              <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-3">Shipping address</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input value={form.address_name} onChange={set("address_name")} placeholder="Full name" data-testid="profile-addr-name" className="sm:col-span-2 px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_line1} onChange={set("address_line1")} placeholder="Address line 1" data-testid="profile-addr-line1" className="sm:col-span-2 px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_line2} onChange={set("address_line2")} placeholder="Address line 2" className="sm:col-span-2 px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_city} onChange={set("address_city")} placeholder="City" data-testid="profile-addr-city" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_region} onChange={set("address_region")} placeholder="State / region" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_postal} onChange={set("address_postal")} placeholder="Postal code" data-testid="profile-addr-postal" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <input value={form.address_phone} onChange={set("address_phone")} placeholder="Mobile" data-testid="profile-addr-phone" className="px-3 py-2.5 border border-aura-border outline-none text-sm" />
                <select value={form.address_country} onChange={set("address_country")} data-testid="profile-addr-country" className="px-3 py-2.5 border border-aura-border outline-none text-sm bg-aura-surface">
                  {(countries || []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
            </div>
          </>
        )}

        <div>
          <label className="text-[10px] tracking-luxe uppercase text-aura-muted">Style preferences</label>
          <input
            value={form.style_preferences} onChange={set("style_preferences")}
            placeholder="e.g. minimalist, classic, glamour"
            data-testid="profile-style"
            className="mt-2 w-full px-3 py-2.5 border border-aura-border focus:border-aura-accent outline-none text-sm"
          />
        </div>

        {alerts.length > 0 && (
          <div data-testid="price-alerts-list">
            <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-3">Price-drop alerts</div>
            <div className="divide-y divide-aura-border border border-aura-border">
              {alerts.map((a) => (
                <div key={a.id || a.listing_id} className="flex items-start justify-between gap-3 px-4 py-3">
                  <Link
                    to={`/product/${encodeURIComponent(a.listing_id)}`}
                    className="min-w-0 hover:text-aura-accent"
                  >
                    <div className="text-sm">{a.title || a.listing_id}</div>
                    <div className="text-xs text-aura-muted mt-1">
                      {a.status === "triggered"
                        ? `Dropped to ${formatMoney(a.triggered_price, a.currency || "USD")}`
                        : `Watching below ${formatMoney(a.target_price, a.currency || "USD")}`}
                      {a.status === "watching" ? " · waiting for a drop" : ""}
                    </div>
                  </Link>
                  <button
                    type="button"
                    onClick={() => removeAlert(a)}
                    disabled={removingId === a.id}
                    data-testid="price-alert-remove-profile"
                    className="shrink-0 text-xs link-underline disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <button onClick={save} disabled={saving} data-testid="save-profile-btn" className="w-full py-3.5 bg-aura-accent text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save profile
        </button>
      </div>
    </div>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (window.location.hash === "#password") {
      document.getElementById("password")?.scrollIntoView({ block: "start" });
    }
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (next !== again) {
      toast.error("The new passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/password", { current_password: current, new_password: next });
      setCurrent("");
      setNext("");
      setAgain("");
      toast.success("Password updated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form id="password" onSubmit={save} className="bg-aura-surface border border-aura-border p-8 space-y-4 mb-8" data-testid="password-form">
      <div className="font-medium">Password</div>
      <p className="text-sm text-aura-muted">Use at least 8 characters. This replaces the password you sign in with.</p>
      <input required type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current password" data-testid="password-current" className="w-full px-3 py-2.5 border border-aura-border outline-none text-sm" />
      <input required type="password" autoComplete="new-password" minLength={8} value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password" data-testid="password-new" className="w-full px-3 py-2.5 border border-aura-border outline-none text-sm" />
      <input required type="password" autoComplete="new-password" minLength={8} value={again} onChange={(e) => setAgain(e.target.value)} placeholder="Repeat new password" data-testid="password-repeat" className="w-full px-3 py-2.5 border border-aura-border outline-none text-sm" />
      <button type="submit" disabled={busy} data-testid="password-save" className="inline-flex items-center gap-2 px-5 py-2.5 btn-accent text-sm rounded-full disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update password
      </button>
    </form>
  );
}
